import { useState, useCallback } from 'react';
import { DeviceIdentityService } from '@/modules/devices/infrastructure/identity/DeviceIdentityService';
import { RemoteDeviceRepository } from '@/modules/devices/infrastructure/repositories/RemoteDeviceRepository';
import { EnrollOrLookupDeviceUseCase } from '@/modules/devices/application/use-cases/EnrollOrLookupDeviceUseCase';
import { db } from '@/shared/db/database';
import { DeviceAlreadyRegisteredError, LicenseExceededError, TimezoneRejectedError } from '@/modules/devices/domain/errors/DeviceErrors';
import { normalizePgTimezone, saveDeviceTimezone } from '@/shared/utils/timezone';

export type EnrollStatus = 'idle' | 'pending' | 'success' | 'error';
export type EnrollErrorKind = 'already-registered' | 'license-exceeded' | 'timezone-rejected' | 'failed' | null;

export interface UseEnrollDeviceOnLoginResult {
  /** Awaitable — LoginPage waits for this before navigating */
  trigger: (idInstance: number, backendTimezone: string) => Promise<void>;
  status: EnrollStatus;
  errorKind: EnrollErrorKind;
  /** The rejected timezone value — only set when errorKind is 'timezone-rejected'. */
  errorDetail: string | null;
  dismiss: () => void;
  setEnrollResult: (result: { status: string; errorKind: string | null }) => void;
}

const ENROLLED_DEVICE_KEY = 'mi_enrolled_device_id';
const ENROLLED_DEVICE_SERIAL_KEY = 'mi_enrolled_device_serial';
const ENROLLED_DEVICE_MODEL_KEY = 'mi_enrolled_device_model';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 10;

async function persistDeviceId(idDevice: number): Promise<void> {
  const value = String(idDevice);
  localStorage.setItem(ENROLLED_DEVICE_KEY, value);
  try {
    if (!db.isOpen()) await db.open();
    await db.settings.put({ key: ENROLLED_DEVICE_KEY, value, updated_at: Date.now() });
  } catch { /* IDB non-fatal */ }
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${ENROLLED_DEVICE_KEY}=${value}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Strict${secure}`;
}

async function persistDeviceInfo(idDevice: number, serial: string, modelName: string): Promise<void> {
  await persistDeviceId(idDevice);
  
  if (serial) {
    localStorage.setItem(ENROLLED_DEVICE_SERIAL_KEY, serial);
    try {
      if (!db.isOpen()) await db.open();
      await db.settings.put({ key: ENROLLED_DEVICE_SERIAL_KEY, value: serial, updated_at: Date.now() });
    } catch { /* IDB non-fatal */ }
  }
  
  if (modelName) {
    localStorage.setItem(ENROLLED_DEVICE_MODEL_KEY, modelName);
    try {
      if (!db.isOpen()) await db.open();
      await db.settings.put({ key: ENROLLED_DEVICE_MODEL_KEY, value: modelName, updated_at: Date.now() });
    } catch { /* IDB non-fatal */ }
  }
}

export function useEnrollDeviceOnLogin(): UseEnrollDeviceOnLoginResult {
  const [status, setStatus] = useState<EnrollStatus>('idle');
  const [errorKind, setErrorKind] = useState<EnrollErrorKind>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);

  const trigger = useCallback(async (idInstance: number, backendTimezone: string): Promise<void> => {
    setStatus('pending');
    setErrorKind(null);
    setErrorDetail(null);
    try {
      const identitySvc = new DeviceIdentityService(db);
      // Generate device ID scoped to this instance
      const idUnique = await identitySvc.getOrCreateIdUnique(idInstance);
      
      // Compare backend timezone vs browser timezone to detect location changes
      // - Backend timezone = user's preference (user.settings_user.timezone_pg)
      // - Browser timezone = actual current location (Intl.DateTimeFormat)
      //
      // If they MATCH → user is in their configured location, use backend timezone
      // If they DON'T MATCH → user is in a different location, use browser timezone
      //
      // Example:
      //   Backend: "Mexico/General", Browser: "Mexico/General" → Use backend ✅
      //   Backend: "Mexico/General", Browser: "America/Bogota" → Use browser (Colombia) ✅
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const normalizedBrowser = normalizePgTimezone(browserTimezone);
      const normalizedBackend = normalizePgTimezone(backendTimezone);
      
      // Use browser timezone if different from backend (location change detected)
      const timezone = normalizedBrowser !== normalizedBackend 
        ? normalizedBrowser 
        : normalizedBackend;
      
      console.log('[useEnrollDeviceOnLogin] Timezone comparison:', {
        backend: normalizedBackend,
        browser: normalizedBrowser,
        selected: timezone,
        locationChanged: normalizedBrowser !== normalizedBackend,
      });
      
      // Update Dexie with selected timezone
      await saveDeviceTimezone(timezone).catch(err => {
        console.warn('[useEnrollDeviceOnLogin] Failed to save timezone to Dexie:', err);
      });

      const repo = new RemoteDeviceRepository();
      const useCase = new EnrollOrLookupDeviceUseCase(repo);
      const result = await useCase.execute({ idUnique, timezone });

      const serial = result.device.serialNumber || '';
      const model = result.device.name || '';
      
      // Persist device info (id, serial, model)
      await persistDeviceInfo(result.device.idDevice, serial, model);
      
      setStatus('success');
      setErrorKind(null);
    } catch (error) {
      console.error('[useEnrollDeviceOnLogin] Device enrollment failed:', error);
      setStatus('error');
      if (error instanceof DeviceAlreadyRegisteredError) {
        setErrorKind('already-registered');
      } else if (error instanceof LicenseExceededError) {
        setErrorKind('license-exceeded');
      } else if (error instanceof TimezoneRejectedError) {
        setErrorKind('timezone-rejected');
        setErrorDetail(error.timezone);
      } else {
        setErrorKind('failed');
      }
    }
  }, []);

  const dismiss = useCallback((): void => {
    setStatus('idle');
    setErrorKind(null);
    setErrorDetail(null);
  }, []);

  const setEnrollResult = useCallback((result: { status: string; errorKind: string | null }): void => {
    setStatus(result.status as EnrollStatus);
    setErrorKind(result.errorKind as EnrollErrorKind);
  }, []);

  return { trigger, status, errorKind, errorDetail, dismiss, setEnrollResult };
}
